import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ReactComponent as CaretDownIcon } from 'assets/icon-angle-down.svg';
import { ReactComponent as CaretUpIcon } from 'assets/icon-angle-up.svg';

import useOutsideClick from 'hooks/useOutsideClick';

import { DropdownOption } from 'shared/prop-types/ReducerProps';
import { colors } from 'shared/defaultTheme';

import { CustomSelectWrapper } from './styles';

const CustomSelect = ({ options, className, backgroundColor, defaultValue, onChange }) => {
  const mainElementRef = useRef();
  const wrapperRef = useRef();
  const { outClick, setOutClick } = useOutsideClick(wrapperRef);
  const [selectedOption, setSelectedOption] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setSelectedOption(options.find((option) => option.value === defaultValue)?.name ?? '');
  }, [defaultValue, options]);

  const onToggle = () => setOpen((currentOpen) => !currentOpen);

  const onSelect = (value) => {
    onChange({ target: { value } });
    onToggle();
  };

  useEffect(() => {
    if (open && outClick) {
      onToggle();
      setOutClick(false);
    }
  }, [outClick, open]);

  return (
    <>
      <CustomSelectWrapper
        className={className}
        backgroundColor={backgroundColor}
        onClick={onToggle}
        ref={mainElementRef}
        aria-hidden
      >
        <span className="default-text">{selectedOption}</span>
        {open ? <CaretUpIcon /> : <CaretDownIcon />}
      </CustomSelectWrapper>
      {open && (
        <CustomSelectWrapper
          className={classNames(['open', className])}
          backgroundColor={colors.white}
          top={mainElementRef.current.offsetTop}
          left={mainElementRef.current.offsetLeft}
          ref={wrapperRef}
        >
          {options.map((option) => (
            <React.Fragment key={option.value}>
              <button
                type="button"
                className={classNames(['item', { selected: option.value === defaultValue }])}
                onClick={() => onSelect(option.value)}
              >
                <div className="text-container">{option.name}</div>
              </button>
            </React.Fragment>
          ))}
        </CustomSelectWrapper>
      )}
    </>
  );
};

CustomSelect.defaultProps = {
  className: '',
  backgroundColor: colors.white,
  defaultValue: '',
  onChange: () => null,
};

CustomSelect.propTypes = {
  options: PropTypes.arrayOf(DropdownOption).isRequired,
  className: PropTypes.string,
  backgroundColor: PropTypes.string,
  defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func,
};

export default CustomSelect;
